/**
 * Send one bill's report: re-check it is still sendable, read its figures,
 * fill the email, render BillReportScreen, capture it and send it.
 *
 * This is the body of the old in-browser bulk loop, moved here unchanged so the
 * background worker's print page runs exactly what the dialog ran. It never
 * throws — every outcome is a SendOneResult the worker records.
 */
import type { BvBill } from '../types/billTypes';
import type { ApiResponse } from '../types/billValidationTypes';
import type { BvEmailTemplate, BvReportContext, BvReportSendResult } from '../services/bvReportService';
import { composeReportEmail } from './bulkSend';
import { canSend, reportSendStatus, SEND_STATUS_LABELS, type SendHistory } from './reportStatus';
import type { ReportFormat, SendOneInput, SendOneResult } from './reportBatchTypes';

export type SendOneDeps = {
  template: BvEmailTemplate | null;
  loadBill: (billId: string) => Promise<ApiResponse<{ bill: BvBill | null; history: SendHistory }>>;
  loadContext: (billId: string) => Promise<ApiResponse<BvReportContext>>;
  /** Mount BillReportScreen for this bill and return the node to capture. */
  render: (bill: BvBill) => Promise<HTMLElement>;
  capture: (target: HTMLElement, format: ReportFormat, subject: string) => Promise<Blob>;
  send: (payload: {
    billId: string; accountId: string | null; recipients: string[]; subject: string; bodyHtml: string;
    report: { format: ReportFormat; file: Blob };
  }) => Promise<ApiResponse<BvReportSendResult>>;
};

export async function sendOneBillReport(input: SendOneInput, deps: SendOneDeps): Promise<SendOneResult> {
  try {
    if (input.recipients.length === 0) return { state: 'skipped', reason: 'No recipient on file for this client' };

    // Re-read: the batch may have waited, and the PDF, the email and the
    // "still not sent" check must all agree on one set of figures.
    const fresh = await deps.loadBill(input.billId);
    if (!fresh.success || !fresh.data) return { state: 'failed', error: fresh.error ?? 'Could not re-read this bill' };
    const { bill, history } = fresh.data;
    if (!bill) return { state: 'skipped', reason: 'This bill no longer exists or was archived' };
    if (!canSend(bill, history)) {
      const current = reportSendStatus(bill, history);
      return {
        state: 'skipped',
        reason: current === 'no_report'
          ? 'This bill no longer has a report — it needs an expected invoice and a retailer invoice'
          : `Already sent since the batch was created (now: ${SEND_STATUS_LABELS[current]})`,
      };
    }

    const context = await deps.loadContext(input.billId);
    if (!context.success || !context.data) return { state: 'failed', error: context.error ?? 'Could not read this bill’s figures' };

    const { subject, bodyHtml } = composeReportEmail(deps.template, context.data.tokens);
    const target = await deps.render(bill);
    const file = await deps.capture(target, input.format, subject);
    const res = await deps.send({
      billId: input.billId, accountId: input.accountId, recipients: input.recipients, subject, bodyHtml,
      report: { format: input.format, file },
    });
    // The server's in-flight guard: another send of this bill is under way.
    if (!res.success && res.code === 'ALREADY_SENDING') {
      return { state: 'skipped', reason: res.error ?? 'This report is already being sent' };
    }
    if (!res.success || !res.data) return { state: 'failed', error: res.error ?? 'The report could not be sent' };
    return { state: 'sent', sendId: res.data.sendId };
  } catch (err) {
    return { state: 'failed', error: err instanceof Error ? err.message : 'The report could not be sent' };
  }
}
